// Cálculos de la ficha del vendedor: rankings, benchmarks y mix por categoría. Puro.
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { lineNetCents, soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import {
    lineCategoryKey,
    lineCategoryLabel,
    UNCATEGORIZED_LINE,
    type LineCategoryKey,
} from '@/shared/patterns/SaleTicket/ticketSummary';
import type { SellerRow } from './sellersTable';

export interface RankPosition {
    position: number;
    total: number;
}

/** Lugar por colocación entre `peers`; sin colocación no tiene lugar. */
function rankAmong(row: SellerRow, peers: readonly SellerRow[]): RankPosition | null {
    if ((row.placedCents ?? 0) <= 0) return null;
    const ranked = peers
        .filter((peer) => (peer.placedCents ?? 0) > 0)
        .sort((a, b) => (b.placedCents ?? 0) - (a.placedCents ?? 0));
    const index = ranked.findIndex((peer) => peer.sellerId === row.sellerId);
    return index === -1 ? null : { position: index + 1, total: ranked.length };
}

export function storeRank(row: SellerRow, rows: readonly SellerRow[]): RankPosition | null {
    if (row.storeId === null) return null;
    return rankAmong(
        row,
        rows.filter((peer) => peer.storeId === row.storeId),
    );
}

export function networkRank(row: SellerRow, rows: readonly SellerRow[]): RankPosition | null {
    return rankAmong(row, rows);
}

export function median(values: readonly number[]): number | null {
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export interface Benchmark {
    value: number | null;
    storeMedian: number | null;
    networkMedian: number | null;
}

export interface SellerBenchmarks {
    sales: Benchmark;
    placed: Benchmark;
    averageTicket: Benchmark;
}

/** Mediana de los que vendieron en el periodo; la de tienda excluye al propio vendedor. */
export function sellerBenchmarks(row: SellerRow, rows: readonly SellerRow[]): SellerBenchmarks {
    const sold = rows.filter((peer) => (peer.salesCount ?? 0) > 0);
    const storePeers = sold.filter(
        (peer) =>
            peer.storeId !== null && peer.storeId === row.storeId && peer.sellerId !== row.sellerId,
    );
    const pick = (field: 'salesCount' | 'placedCents' | 'averageTicketCents'): Benchmark => {
        const valuesOf = (list: readonly SellerRow[]) =>
            list.map((peer) => peer[field]).filter((value): value is number => value !== null);
        return {
            value: row[field],
            storeMedian: median(valuesOf(storePeers)),
            networkMedian: median(valuesOf(sold)),
        };
    };
    return {
        sales: pick('salesCount'),
        placed: pick('placedCents'),
        averageTicket: pick('averageTicketCents'),
    };
}

export type VersusTone = 'good' | 'bad' | 'flat';

/** Diferencia contra la mediana de la red; menos de 3% se lee «≈ igual». */
export function versusNetwork(
    value: number | null,
    base: number | null,
): { tone: VersusTone; label: string } {
    if (value === null || base === null || base <= 0) return { tone: 'flat', label: '—' };
    const pct = Math.round(((value - base) / base) * 100);
    if (Math.abs(pct) < 3) return { tone: 'flat', label: '≈ igual' };
    return pct > 0
        ? { tone: 'good', label: `+${String(pct)}% vs red` }
        : { tone: 'bad', label: `${String(pct)}% vs red` };
}

export const UNCATEGORIZED = UNCATEGORIZED_LINE;
export type SaleCategory = LineCategoryKey;

export const categoryLabel = lineCategoryLabel;

type CategorySale = Pick<
    BackofficeSale,
    'lines' | 'cancelledAt' | 'amountCents' | 'equipmentPriceCents' | 'soldCents'
>;

/** Categorías de la venta según sus líneas (sin regalos); sin líneas, «Sin categoría». */
export function saleCategories(sale: CategorySale): SaleCategory[] {
    const lines = (sale.lines ?? []).filter((line) => line.role !== 'GIFT');
    if (lines.length === 0) return [UNCATEGORIZED];
    return [...new Set(lines.map(lineCategoryKey))];
}

export interface CategorySlice {
    category: SaleCategory;
    label: string;
    salesCount: number;
    amountCents: number;
}

/** Tickets con al menos una línea de la categoría y monto de esas líneas, sin canceladas.
 * Orden alfabético; «Sin categoría» al final. */
export function categoryMix(sales: readonly CategorySale[]): CategorySlice[] {
    const totals = new Map<SaleCategory, { salesCount: number; amountCents: number }>();
    const add = (category: SaleCategory, amountCents: number, countSale: boolean) => {
        const slot = totals.get(category) ?? { salesCount: 0, amountCents: 0 };
        slot.amountCents += amountCents;
        if (countSale) slot.salesCount += 1;
        totals.set(category, slot);
    };
    for (const sale of sales) {
        if (sale.cancelledAt !== null) continue;
        const lines = (sale.lines ?? []).filter((line) => line.role !== 'GIFT');
        if (lines.length === 0) {
            add(UNCATEGORIZED, soldCentsOf(sale), true);
            continue;
        }
        const counted = new Set<SaleCategory>();
        for (const line of lines) {
            const category = lineCategoryKey(line);
            add(category, lineNetCents(line), !counted.has(category));
            counted.add(category);
        }
    }
    return sortSlices(
        [...totals.entries()].map(([category, slot]) => ({
            category,
            label: categoryLabel(category),
            ...slot,
        })),
    );
}

/** Orden alfabético por etiqueta; «Sin categoría» al final. */
function sortSlices(slices: CategorySlice[]): CategorySlice[] {
    return slices.sort((a, b) => {
        if (a.category === UNCATEGORIZED) return 1;
        if (b.category === UNCATEGORIZED) return -1;
        return a.label.localeCompare(b.label, 'es-MX');
    });
}

/** El mix con las categorías `base` siempre presentes, en cero si no tuvieron tickets. */
export function categoryMixWithBase(
    sales: readonly CategorySale[],
    base: readonly SaleCategory[],
): CategorySlice[] {
    const mix = categoryMix(sales);
    const present = new Set(mix.map((slice) => slice.category));
    const missing = base
        .filter((category) => !present.has(category))
        .map((category) => ({
            category,
            label: categoryLabel(category),
            salesCount: 0,
            amountCents: 0,
        }));
    return sortSlices([...mix, ...missing]);
}
